import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loading } from "views/components";
import { isMediaVideoFormat } from "lib/mediaSourceHelpers";
import { DesignSizeCategories } from "../../DesignSizeCategories";
import FileIcon from "views/components/icons/FileIcon";
import SizesIcon from "views/components/icons/SizesIcon";
import DesignSizeLabel from "views/components/DesignSizeLabel";
import style from "./style.module.css";

interface Design {
  id: string;
  title: string;
  templateSize: string;
  thumbnailUrl: string;
  width: number;
  height: number;
  collectionId: string;
  templateCode: string;
  unit?: string;
  measurementUnits?: string;
  widthInMm?: number;
  heightInMm?: number;
  widthInInches?: number;
  heightInInches?: number;
}

interface Category {
  name: string;
  designs: Design[];
}

interface MobileCreateDraftSheetProps {
  isOpen: boolean;
  selectedDesign: Design;
  collectionDesignsCategorised: Category[];
  sizeCount: number;
  isLoading: boolean;
  onCloseModal: () => void;
  createDraftFromTemplate: (args: { design: Design }) => void;
  copyCollectionToDrafts: (args: {
    collectionId: string;
    templateCode: string;
  }) => void;
}

const SWIPE_DISMISS_THRESHOLD = 80;

const MobileCreateDraftSheet = ({
  isOpen,
  selectedDesign,
  collectionDesignsCategorised,
  sizeCount,
  isLoading,
  onCloseModal,
  createDraftFromTemplate,
  copyCollectionToDrafts
}: MobileCreateDraftSheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const currentTranslateY = useRef(0);
  const [previewDesign, setPreviewDesign] = useState<Design | null>(null);

  const designToDisplay = previewDesign || selectedDesign;
  const isCollection = sizeCount > 1;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartY.current = touch.clientY;
    currentTranslateY.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!sheetRef.current || !touch) return;
    const deltaY = touch.clientY - touchStartY.current;
    const translate = Math.max(0, deltaY);
    currentTranslateY.current = translate;
    sheetRef.current.style.transform = `translateY(${translate}px)`;
    sheetRef.current.style.transition = "none";
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!sheetRef.current) return;
    sheetRef.current.style.transition = "";

    if (currentTranslateY.current > SWIPE_DISMISS_THRESHOLD) {
      onCloseModal();
    } else {
      sheetRef.current.style.transform = "";
    }
    currentTranslateY.current = 0;
  }, [onCloseModal]);

  const handleClose = useCallback(() => {
    setPreviewDesign(null);
    onCloseModal();
  }, [onCloseModal]);

  const handleOpenDesign = useCallback(() => {
    const design = previewDesign || selectedDesign;
    createDraftFromTemplate({ design });
    handleClose();
  }, [previewDesign, selectedDesign, createDraftFromTemplate, handleClose]);

  const handleOpenCollection = useCallback(() => {
    const design = previewDesign || selectedDesign;
    const { collectionId, templateCode } = design;
    copyCollectionToDrafts({ collectionId, templateCode });
    handleClose();
  }, [previewDesign, selectedDesign, copyCollectionToDrafts, handleClose]);

  const handleChangePreview = useCallback(
    (design: Design) => {
      if (previewDesign && previewDesign.id === design.id) {
        setPreviewDesign(null);
      } else {
        setPreviewDesign(design);
      }
    },
    [previewDesign]
  );

  const isHighlighted = useCallback(
    (design: Design) => {
      const current = previewDesign || selectedDesign;
      return current && current.id === design.id;
    },
    [previewDesign, selectedDesign]
  );

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className={style.backdrop}
        data-open={isOpen}
        onClick={handleClose}
      />
      <div
        ref={sheetRef}
        className={style.sheet}
        data-open={isOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Template preview"
      >
        <div
          className={style.handleArea}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={style.handleBar} />
        </div>

        <div className={style.sheetContent}>
          {isLoading ? (
            <div className={style.loadingWrapper}>
              {/* @ts-expect-error JS component has loosely typed props */}
              <Loading />
            </div>
          ) : (
            <>
              <div className={style.previewContainer}>
                {designToDisplay &&
                  (isMediaVideoFormat(designToDisplay.thumbnailUrl) ? (
                    <video muted loop autoPlay>
                      <source
                        src={designToDisplay.thumbnailUrl}
                        type="video/mp4"
                      />
                    </video>
                  ) : (
                    <img
                      src={designToDisplay.thumbnailUrl}
                      alt={`${designToDisplay.title} - ${designToDisplay.templateSize}`}
                    />
                  ))}
              </div>

              <div className={style.infoSection}>
                <div className={style.title}>{designToDisplay?.title}</div>
                <div className={style.sizeLabel}>
                  {designToDisplay && (
                    <DesignSizeLabel
                      design={designToDisplay}
                      as="span"
                      withMeasurements
                    />
                  )}
                </div>
              </div>

              {isCollection && (
                <div className={style.sizeCategoriesSection}>
                  <DesignSizeCategories
                    collectionDesignsCategorised={collectionDesignsCategorised}
                    handleChangePreview={handleChangePreview}
                    isHighlighted={isHighlighted}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {!isLoading && (
          <div className={style.actions}>
            <button className={style.primaryButton} onClick={handleOpenDesign}>
              {/* @ts-expect-error JS icon component lacks proper TS types */}
              <FileIcon color="#ffffff" size="18" />
              Edit Design
            </button>
            {isCollection && (
              <button
                className={style.secondaryButton}
                onClick={handleOpenCollection}
              >
                {/* @ts-expect-error JS icon component lacks proper TS types */}
                <SizesIcon color="#3184fc" size="18" />
                Edit Collection
              </button>
            )}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default MobileCreateDraftSheet;
